import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { FileText, Printer, Calendar, Download, ChevronRight } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function sourceLabel(t) {
  const s = t.source || "";
  if (s.startsWith("bank:")) {
    const key = s.split(":")[1] || "";
    return `Bank · ${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  }
  if (s === "stripe:deposit") return "Card · Deposit";
  if (s === "stripe:payout") return "Card · Cash-out";
  if (s === "vault") return `Vault${t.vault_type && t.vault_type !== "—" ? ` · ${String(t.vault_type).replace(/_/g, " ")}` : ""}`;
  return s || "—";
}

function signedAmount(t) {
  // For bank rows we have `signed_amount` (negative for debits); for vault rows
  // we infer sign from the `type` field.
  if (typeof t.signed_amount === "number") return t.signed_amount;
  const amt = Number(t.amount || 0);
  const ttype = t.type || "";
  if (ttype.includes("withdraw") || ttype === "card_deposit_rejected") return -amt;
  return amt;
}

function downloadCsv(stmt) {
  if (!stmt) return;
  const T = stmt.totals || {};
  const a = stmt.user?.address;
  const addrRows = a ? [
    [`Address line 1`, a.line1 || ""],
    ...(a.line2 ? [[`Address line 2`, a.line2]] : []),
    [`City`, a.city || ""],
    [`State`, a.state || ""],
    [`Postal`, a.postal || ""],
  ] : [];
  const rows = [
    ["Withdraw Guard · Monthly Statement"],
    [`Month`, stmt.label],
    [`Account holder`, (a && a.name) || stmt.user.name || ""],
    [`Email`, stmt.user.email || ""],
    ...addrRows,
    [],
    ["SUMMARY"],
    ["Vault deposits", (T.deposits || 0).toFixed(2)],
    ["Vault withdrawals", (T.withdrawals || 0).toFixed(2)],
    ["Bank credits (payroll, ACH in, transfers in)", (T.bank_credits || 0).toFixed(2)],
    ["Bank debits (card swipes, ACH out, transfers out)", (T.bank_debits || 0).toFixed(2)],
    ["Payroll (paycheck direct deposits)", (T.payroll || 0).toFixed(2)],
    ["Other", (T.other || 0).toFixed(2)],
    ["Total transactions", T.count || 0],
    [],
    ["BY VAULT"],
    ["Vault", "Deposits", "Withdrawals"],
    ...Object.entries(stmt.by_vault || {}).map(([v, t]) =>
      [v.replace(/_/g, " "), t.deposits.toFixed(2), t.withdrawals.toFixed(2)]
    ),
    [],
    ["BY BANK ACCOUNT"],
    ["Account", "Credits", "Debits", "Count"],
    ...Object.entries(stmt.by_account || {}).map(([k, t]) =>
      [k.charAt(0).toUpperCase() + k.slice(1), t.credits.toFixed(2), t.debits.toFixed(2), t.count]
    ),
    [],
    ["TRANSACTIONS"],
    ["Date", "Source", "Merchant / Counterparty", "Action", "Type", "Signed amount (USD)", "Amount (USD)", "Status", "Payroll", "Settled", "Transfer ID"],
    ...stmt.items.map(t => [
      t.created_at ? new Date(t.created_at).toLocaleString() : "",
      sourceLabel(t),
      t.description,
      t.subtype || "",
      t.type,
      signedAmount(t).toFixed(2),
      Number(t.amount || 0).toFixed(2),
      t.status || "",
      t.payroll ? "yes" : "",
      t.settled ? "yes" : "no",
      t.transfer_id || "",
    ]),
  ];
  const csv = rows.map(r => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `withdraw-guard-statement-${stmt.month}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function Statements() {
  const { user } = useAuth();
  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState(null);   // "YYYY-MM"
  const [stmt, setStmt] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await api.get("/statements/months");
        setMonths(r.data);
        if (r.data.length > 0) setSelected(r.data[0].month);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      setErr("");
      try {
        const r = await api.get(`/statements/${selected}`);
        setStmt(r.data);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
  }, [selected]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-cream" data-testid="statements-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Tax documents</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Monthly statements.</h1>
        <p className="text-moss mt-3 max-w-2xl text-sm sm:text-base">
          A printable statement for every month you had activity — for your records, your accountant, or your tax filing.
        </p>

        {err && <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="statements-error">{err}</div>}

        {loading ? (
          <div className="mt-10 font-mono text-sm text-sage">loading…</div>
        ) : months.length === 0 ? (
          <div className="mt-10 bg-linen border border-sand rounded-2xl p-10 text-center text-sage"
               data-testid="statements-empty">
            No activity yet — statements will appear here after your first transaction.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Month picker */}
            <aside className="md:col-span-1" data-testid="statements-month-list">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mb-2">Select a month</div>
              <ul className="space-y-1">
                {months.map((m) => {
                  const label = new Date(`${m.month}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
                  const active = m.month === selected;
                  return (
                    <li key={m.month}>
                      <button onClick={() => setSelected(m.month)}
                        className={`w-full text-left rounded-lg px-3 py-2.5 text-sm border flex items-center justify-between gap-2 transition ${
                          active ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-forest"
                        }`}
                        data-testid={`statements-month-${m.month}`}>
                        <span className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5" />{label}
                        </span>
                        <span className={`text-[10px] font-mono ${active ? "text-sage-mid" : "text-sage"}`}>{m.count} txns</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Statement view */}
            <section className="md:col-span-2">
              {stmt ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-3 print:hidden">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Statement</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => downloadCsv(stmt)}
                              className="inline-flex items-center gap-1.5 bg-white border border-sand text-ink rounded-lg px-3 py-2 text-xs font-medium hover:bg-cream-warm"
                              data-testid="statements-csv-btn">
                        <Download className="w-3.5 h-3.5" /> CSV
                      </button>
                      <button onClick={() => window.print()}
                              className="inline-flex items-center gap-1.5 bg-forest text-white rounded-lg px-3 py-2 text-xs font-medium hover:bg-forest-deep"
                              data-testid="statements-print-btn">
                        <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-sand rounded-2xl overflow-hidden" id="printable-statement"
                       data-testid="statement-printable">
                    <div className="bg-ink text-cream px-5 py-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage-mid">Withdraw Guard · Monthly statement</div>
                      <h2 className="font-serif text-2xl mt-1">{stmt.label}</h2>
                      <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-6 text-xs text-sage-mid font-mono" data-testid="statement-holder-block">
                        <div>
                          <div className="text-white">{stmt.user.address?.name || stmt.user.name}</div>
                          <div>{stmt.user.email}</div>
                        </div>
                        {stmt.user.address && (
                          <div className="sm:text-right">
                            <div className="text-white">{stmt.user.address.line1}</div>
                            {stmt.user.address.line2 && <div className="text-white">{stmt.user.address.line2}</div>}
                            <div>
                              {stmt.user.address.city}
                              {stmt.user.address.city && (stmt.user.address.state || stmt.user.address.postal) ? ", " : ""}
                              {stmt.user.address.state} {stmt.user.address.postal}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-5 space-y-5">
                      {/* Summary tiles */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="statement-summary">
                        <SummaryTile label="Vault deposits" value={money(stmt.totals.deposits)} />
                        <SummaryTile label="Vault withdrawals" value={money(stmt.totals.withdrawals)} />
                        <SummaryTile label="Payroll" value={money(stmt.totals.payroll || 0)} />
                        <SummaryTile label="Bank credits" value={money(stmt.totals.bank_credits || 0)} />
                        <SummaryTile label="Bank debits" value={money(stmt.totals.bank_debits || 0)} />
                        <SummaryTile label="Total txns" value={stmt.totals.count} mono={false} />
                      </div>

                      {/* By bank account (Spending/Savings/Bills) */}
                      {Object.keys(stmt.by_account || {}).length > 0 && (
                        <div data-testid="statement-by-account">
                          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mb-2">By bank account</div>
                          <table className="w-full text-sm border border-sand-warm rounded-lg overflow-hidden">
                            <thead className="bg-cream-warm text-[10px] uppercase tracking-[0.18em] text-sage">
                              <tr>
                                <th className="text-left px-3 py-2">Account</th>
                                <th className="text-right px-3 py-2">Credits</th>
                                <th className="text-right px-3 py-2">Debits</th>
                                <th className="text-right px-3 py-2">Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(stmt.by_account).map(([k, tot]) => (
                                <tr key={k} className="border-t border-sand-warm">
                                  <td className="px-3 py-2 capitalize">{k}</td>
                                  <td className="px-3 py-2 text-right font-mono text-forest">{money(tot.credits)}</td>
                                  <td className="px-3 py-2 text-right font-mono text-clay">{money(tot.debits)}</td>
                                  <td className="px-3 py-2 text-right font-mono">{tot.count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Vault breakdown */}
                      {Object.keys(stmt.by_vault).length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mb-2">By vault</div>
                          <table className="w-full text-sm border border-sand-warm rounded-lg overflow-hidden">
                            <thead className="bg-cream-warm text-[10px] uppercase tracking-[0.18em] text-sage">
                              <tr><th className="text-left px-3 py-2">Vault</th><th className="text-right px-3 py-2">Deposits</th><th className="text-right px-3 py-2">Withdrawals</th></tr>
                            </thead>
                            <tbody>
                              {Object.entries(stmt.by_vault).map(([v, tot]) => (
                                <tr key={v} className="border-t border-sand-warm">
                                  <td className="px-3 py-2 capitalize">{v.replace(/_/g, " ")}</td>
                                  <td className="px-3 py-2 text-right font-mono">{money(tot.deposits)}</td>
                                  <td className="px-3 py-2 text-right font-mono">{money(tot.withdrawals)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Line items */}
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mb-2">Transactions</div>
                        <ul className="divide-y divide-sand-warm border border-sand-warm rounded-lg overflow-hidden" data-testid="statement-items">
                          {stmt.items.map((t) => {
                            const signed = signedAmount(t);
                            const src = String(t.source || "");
                            const isBank = src.startsWith("bank:");
                            const isStripe = src.startsWith("stripe:");
                            const badgeCls = isBank
                              ? "bg-mint-light text-forest"
                              : isStripe
                                ? "bg-[#EFEEF6] text-[#5B4E8A]"
                                : "bg-linen text-[#5B4A3A]";
                            return (
                              <li key={t.id} className="px-3 py-2.5 text-xs sm:text-sm flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="text-ink font-medium break-words uppercase tracking-wide text-[13px]">
                                    {t.description}
                                  </div>
                                  {t.subtype && (
                                    <div className="text-[11px] text-sage mt-0.5">{t.subtype}</div>
                                  )}
                                  <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px] text-sage font-mono">
                                    <span>{new Date(t.created_at).toLocaleString()}</span>
                                    <span className={`px-1.5 py-0.5 rounded ${badgeCls}`}>
                                      {sourceLabel(t)}
                                    </span>
                                    {t.payroll && <span className="px-1.5 py-0.5 rounded bg-mint-light text-forest">payroll</span>}
                                    {t.settled && <span className="text-forest">settled</span>}
                                  </div>
                                </div>
                                <div className={`text-right font-mono shrink-0 ${signed < 0 ? "text-clay" : "text-ink"}`}>
                                  {signed < 0 ? "-" : (isBank || isStripe ? "+" : "")}{money(Math.abs(signed))}
                                </div>
                              </li>
                            );
                          })}
                          {stmt.items.length === 0 && (
                            <li className="px-3 py-6 text-center text-xs text-sage">No activity in this month.</li>
                          )}
                        </ul>
                      </div>

                      <div className="text-[10px] text-sage pt-3 border-t border-sand-warm">
                        This statement is generated by Withdraw Guard for your records. Not an official tax form.
                        For tax filing, use in combination with any 1099s issued by our banking provider.
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-sage">Loading statement…</div>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Print styles: hide everything except the statement */}
      <style>{`
        @media print {
          body { background: white !important; }
          header, aside, nav, .print\\:hidden { display: none !important; }
          #printable-statement { border: none !important; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

function SummaryTile({ label, value, mono = true }) {
  return (
    <div className="bg-cream-warm border border-sand-warm rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{label}</div>
      <div className={`mt-1 text-ink text-lg ${mono ? "font-mono" : "font-serif"}`}>{value}</div>
    </div>
  );
}
